/* global chrome */

/**
 * Storage abstraction layer that handles localStorage unavailability in iframes
 * and browser extension contexts by falling back through multiple storage mechanisms
 * 
 * Fallback order:
 * 1. chrome.storage.local (Chrome extensions, works in iframes)
 * 2. localStorage (standard persistent storage)
 * 3. sessionStorage (session-only, may work in Firefox iframes)
 * 4. In-memory storage (last resort for restricted iframes)
 */
export class StorageAdapter {
	// In-memory storage as last resort for restricted iframes (e.g., Firefox)
	private static memoryStorage: Map<string, string> = new Map();

	/**
	 * Check if we're in a browser extension context with chrome.storage available
	 */
	static isExtensionContext(): boolean {
		return typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local;
	}

	/**
	 * Get a value from storage (async, works in all contexts including iframes)
	 */
	static async get(key: string): Promise<string | null> {
		// Try chrome.storage first (works in iframes and all extension contexts)
		if (this.isExtensionContext()) {
			try {
				const result = await chrome.storage.local.get([key]);
				return result[key] || null;
			} catch (e) {
				console.error('StorageAdapter: Error getting from chrome.storage', e);
			}
		}

		// Try localStorage
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				return window.localStorage.getItem(key);
			}
		} catch (e) {
			console.error('StorageAdapter: Error getting from localStorage', e);
		}

		// Try sessionStorage (may work in Firefox iframes where localStorage doesn't)
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				const value = window.sessionStorage.getItem(key);
				if (value !== null) {
					return value;
				}
			}
		} catch (e) {
			console.error('StorageAdapter: Error getting from sessionStorage', e);
		}

		// Last resort: in-memory storage (for restricted iframes like Firefox)
		return this.memoryStorage.get(key) || null;
	}

	/**
	 * Set a value in storage (async)
	 */
	static async set(key: string, value: string): Promise<void> {
		let stored = false;

		// Try chrome.storage first
		if (this.isExtensionContext()) {
			try {
				await chrome.storage.local.set({ [key]: value });
				stored = true;
			} catch (e) {
				console.error('StorageAdapter: Error setting in chrome.storage', e);
			}
		}

		// Try localStorage for backward compatibility and cross-context sync
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				window.localStorage.setItem(key, value);
				stored = true;
			}
		} catch (e) {
			/* */
		}

		// Try sessionStorage (may work in Firefox iframes where localStorage doesn't)
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				window.sessionStorage.setItem(key, value);
				stored = true;
			}
		} catch (e) {
			console.error('StorageAdapter: sessionStorage not available', e);
		}

		// Always store in memory as last resort (for restricted iframes like Firefox)
		this.memoryStorage.set(key, value);
		
		if (!stored) {
			console.warn(`StorageAdapter: Using in-memory storage for key "${key}" (iframe restrictions)`);
		}
	}

	/**
	 * Remove a value from storage (async)
	 */
	static async remove(key: string): Promise<void> {
		// Remove from chrome.storage
		if (this.isExtensionContext()) {
			try {
				await chrome.storage.local.remove([key]);
			} catch (e) {
				console.error('StorageAdapter: Error removing from chrome.storage', e);
			}
		}

		// Remove from localStorage
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				window.localStorage.removeItem(key);
			}
		} catch (e) {
			console.error('StorageAdapter: Error removing from localStorage', e);
		}

		// Remove from sessionStorage
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				window.sessionStorage.removeItem(key);
			}
		} catch (e) {
			console.error('StorageAdapter: Error removing from sessionStorage', e);
		}

		// Remove from in-memory storage
		this.memoryStorage.delete(key);
	}

	/**
	 * Synchronous version for backward compatibility
	 * Note: Falls back through multiple storage mechanisms
	 * Prefer using the async methods when possible
	 */
	static getSync(key: string): string | null {
		// Try localStorage first
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				return window.localStorage.getItem(key);
			}
		} catch (e) {
			/* */
		}

		// Try sessionStorage (may work in Firefox iframes)
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				const value = window.sessionStorage.getItem(key);
				if (value !== null) {
					return value;
				}
			}
		} catch (e) {
			console.error('StorageAdapter: sessionStorage not available (sync)', e);
		}

		// Last resort: in-memory storage
		return this.memoryStorage.get(key) || null;
	}

	/**
	 * Synchronous set for backward compatibility
	 * Note: Falls back through multiple storage mechanisms
	 * Prefer using the async methods when possible
	 */
	static setSync(key: string, value: string): void {
		let stored = false;

		// Try localStorage first
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				window.localStorage.setItem(key, value);
				stored = true;
			}
		} catch (e) {
			/* */
		}

		// Try sessionStorage (may work in Firefox iframes)
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				window.sessionStorage.setItem(key, value);
				stored = true;
			}
		} catch (e) {
			console.error('StorageAdapter: sessionStorage not available (sync)', e);
		}

		// Always store in memory as fallback
		this.memoryStorage.set(key, value);

		// Fire and forget for chrome.storage
		if (this.isExtensionContext()) {
			chrome.storage.local.set({ [key]: value }).catch((e) => {
				console.error('StorageAdapter: Error setting in chrome.storage (sync)', e);
			});
		}

		if (!stored) {
			console.warn(`StorageAdapter: Using in-memory storage for key "${key}" (iframe restrictions, sync)`);
		}
	}

	/**
	 * Synchronous remove for backward compatibility
	 */
	static removeSync(key: string): void {
		// Remove from localStorage
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				window.localStorage.removeItem(key);
			}
		} catch (e) {
			console.error('StorageAdapter: Error removing from localStorage (sync)', e);
		}

		// Remove from sessionStorage
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				window.sessionStorage.removeItem(key);
			}
		} catch (e) {
			console.error('StorageAdapter: Error removing from sessionStorage (sync)', e);
		}

		// Remove from in-memory storage
		this.memoryStorage.delete(key);

		// Fire and forget for chrome.storage
		if (this.isExtensionContext()) {
			chrome.storage.local.remove([key]).catch((e) => {
				console.error('StorageAdapter: Error removing from chrome.storage (sync)', e);
			});
		}
	}

	/**
	 * Clear all storage (useful for logout)
	 */
	static async clear(): Promise<void> {
		// Clear chrome.storage
		if (this.isExtensionContext()) {
			try {
				await chrome.storage.local.clear();
			} catch (e) {
				console.error('StorageAdapter: Error clearing chrome.storage', e);
			}
		}

		// Clear localStorage
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				window.localStorage.clear();
			}
		} catch (e) {
			console.error('StorageAdapter: Error clearing localStorage', e);
		}

		// Clear sessionStorage
		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				window.sessionStorage.clear();
			}
		} catch (e) {
			console.error('StorageAdapter: Error clearing sessionStorage', e);
		}

		// Clear in-memory storage
		this.memoryStorage.clear();
	}

	/**
	 * Check if storage is available (for debugging)
	 */
	static getStorageInfo(): { localStorage: boolean; sessionStorage: boolean; chromeStorage: boolean; memoryStorage: boolean } {
		const info = {
			localStorage: false,
			sessionStorage: false,
			chromeStorage: this.isExtensionContext(),
			memoryStorage: true, // always available
		};

		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				window.localStorage.setItem('__test__', 'test');
				window.localStorage.removeItem('__test__');
				info.localStorage = true;
			}
		} catch (e) {
			// localStorage not available
		}

		try {
			if (typeof window !== 'undefined' && window.sessionStorage) {
				window.sessionStorage.setItem('__test__', 'test');
				window.sessionStorage.removeItem('__test__');
				info.sessionStorage = true;
			}
		} catch (e) {
			// sessionStorage not available
		}

		return info;
	}
}
